/*
 *
 * GenericPopUp
 *
 * @version 1.0.0
 * @author Magarelli-Fabrizio
 * @since 04-Oct-2024
 */

import React from 'react'
import PropTypes from 'prop-types'
import MerchantModal from '@nexidigital/merchantmodal'
import Toggle from 'Hoc/ToggleComponent'
import './GenericPopUp.scss'

const GenericPopUp = ({ data, actions, primaryButtonLoading }) => {
  const dispatchActionAndMetod = (datas) => {
    const { type = 'METHOD', payload = false, closePopup = true } = datas
    if (type.toUpperCase() === 'ACTION' && payload) actions.dispatchAction(payload)
    if (type.toUpperCase() === 'METHOD' && payload) payload()
    if (closePopup) actions.closePopup()
  }
  return (
    <Toggle>
      <MerchantModal
        title={data?.title}
        iconName={data?.iconName}
        description={data?.description}
        onClose={() => {
          if (data?.onClose) dispatchActionAndMetod(data.onClose)
        }}
        primaryButtonLabel={data?.primaryButtonLabel}
        primaryAction={() => {
          if (data?.primaryAction) dispatchActionAndMetod(data.primaryAction)
        }}
        primaryButtonDisabled={data?.primaryButtonDisabled}
        primaryButtonLoading={primaryButtonLoading}
        secondaryButtonLabel={data?.secondaryButtonLabel}
        secondaryAction={() => {
          if (data?.secondaryAction) dispatchActionAndMetod(data.secondaryAction)
        }}
        secondaryButtonDisabled={data?.secondaryButtonDisabled}>
        {!!data?.children && typeof data?.children === 'function' ? data.children() : data?.children}
      </MerchantModal>
    </Toggle>
  )
}

GenericPopUp.propTypes = {
  data: PropTypes.shape({
    /** This prop is a string or an object. It represents the title that will be displayed on the modal. */
    title: PropTypes.PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    /** This prop is a string or an object. It represents the icon name that will be displayed on the modal. */
    iconName: PropTypes.PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    /** This prop is a string or an object. It represents the description that will be displayed on the modal. */
    description: PropTypes.PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    /** This prop is a string or an object. It represents the aria label that will be displayed on the modal description. */
    ariaModalDescription: PropTypes.PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    /** This prop is a string or an object. It represents the label that will be displayed on the primary button. */
    primaryButtonLabel: PropTypes.PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    /** This prop can be a function or an object composed as follows: {type='ACTION/METHOD', payload={function to dispatch or execute}, closePopup=true/false} or a boolean that, when set to true, will close the popup, and when set to false, the prop will not be considered **/
    primaryAction: PropTypes.oneOfType([PropTypes.bool, PropTypes.object, PropTypes.func]),
    /** This prop is a boolean. If it's true, the primary button will be disabled. If it's false or not provided, the primary button will not be disabled. */
    primaryButtonDisabled: PropTypes.bool,
    /** This prop is a string or an object. It represents the label that will be displayed on the secondary button. */
    secondaryButtonLabel: PropTypes.PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    /** This prop can be a function or an object composed as follows: {type='ACTION/METHOD', payload={function to dispatch or execute}, closePopup=true/false} or a boolean that, when set to true, will close the popup, and when set to false, the prop will not be considered **/
    secondaryAction: PropTypes.oneOfType([PropTypes.bool, PropTypes.object, PropTypes.func]),
    /** This prop is a boolean. If it's true, the secondary button will be disabled. If it's false or not provided, the secondary button will not be disabled. */
    secondaryButtonDisabled: PropTypes.bool,
    /** This prop can be an a function or an object composed as follows: {type='ACTION/METHOD', payload={function to dispatch or execute}, closePopup=true/false} or a boolean that, when set to true, will close the popup, and when set to false, the prop will not be considered **/
    onClose: PropTypes.oneOfType([PropTypes.bool, PropTypes.object, PropTypes.func]),
    /** This prop is a node. It represents the children that will be displayed on the modal. */
    children: PropTypes.object
  }),
  /** This prop is a boolean. It represents the loading state of the primary button. */
  primaryButtonLoading: PropTypes.bool,
  actions: PropTypes.object
}

GenericPopUp.defaultProps = {
  /* example of use of props.data:
    primaryAction: {type: 'ACTION', payload: actions.doOrderSearch(), closePopup: false},
    onClose: {type: 'METHOD', payload:()=>console.log("ONCLOSE method"), closePopup: false},
  */
  data: {},
  actions: {}
}

export default GenericPopUp
